/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Directive, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatLegacyTooltip as MatTooltip } from '@angular/material/legacy-tooltip';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appCustomTooltip]',
  standalone: true
})
export class CustomTooltipDirective implements OnInit, OnDestroy {
  @Input() tooltipClass = '';
  private styleElement: HTMLStyleElement | null = null;
  private observer: MutationObserver | null = null;

  constructor(
    private renderer: Renderer2,
    private matTooltip: MatTooltip
  ) {}

  ngOnInit() {
    // Get tooltip class from either the directive input or matTooltip's tooltipClass
    let tooltipClass = this.tooltipClass;

    if (!tooltipClass && this.matTooltip.tooltipClass) {
      // Handle different types of tooltipClass
      if (typeof this.matTooltip.tooltipClass === 'string') {
        tooltipClass = this.matTooltip.tooltipClass;
      } else if (Array.isArray(this.matTooltip.tooltipClass)) {
        tooltipClass = this.matTooltip.tooltipClass[0];
      }
    }

    if (tooltipClass) {
      // Set the tooltip class if not already set
      if (!this.matTooltip.tooltipClass) {
        this.matTooltip.tooltipClass = tooltipClass;
      }

      // Create and inject custom styles immediately
      this.injectGlobalStyles(tooltipClass);

      // Also set up an observer to apply styles when tooltip is shown
      this.setupTooltipObserver(tooltipClass);
    }
  }

  ngOnDestroy() {
    if (this.styleElement) {
      this.styleElement.remove();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private injectGlobalStyles(tooltipClass?: string) {
    const className = tooltipClass || this.tooltipClass;
    if (!className) return;

    // Check if styles already exist
    if (document.querySelector(`style[data-tooltip-class="${className}"]`)) {
      return;
    }

    this.styleElement = this.renderer.createElement('style');
    this.styleElement.type = 'text/css';
    this.styleElement.setAttribute('data-tooltip-class', className);

    const customStyles = `
      .mat-tooltip.${className} {
        width: auto !important;
        min-height: 26px !important;
        height: auto !important;
        border-radius: 8px !important;
        opacity: 1 !important;
        padding: 4px 8px !important;
        font-size: 12px !important;
        line-height: 18px !important;
        font-weight: 700 !important;
        font-family: 'Neue Haas Grotesk Text Std' !important;
        background-color: #000000 !important;
        color: white !important;
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        white-space: normal !important;
        max-width: 236px !important;
        box-sizing: border-box !important;
      }
    `;

    this.styleElement.innerHTML = customStyles;
    document.head.appendChild(this.styleElement);
  }

  private setupTooltipObserver(tooltipClass?: string) {
    const className = tooltipClass || this.tooltipClass;
    if (!className) return;

    // Observe for tooltip creation in the overlay container
    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const tooltip = element.querySelector(`.${className}`) || (element.classList.contains(className) ? element : null);

            if (tooltip) {
              // Apply styles directly to the tooltip element if needed
              this.applyStylesToTooltip(tooltip as HTMLElement);
            }
          }
        });
      });
    });

    // Start observing the body for tooltip additions
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private applyStylesToTooltip(tooltip: HTMLElement) {
    // Apply styles directly as a fallback
    const styles = {
      width: 'auto',
      minHeight: '26px',
      height: 'auto',
      borderRadius: '8px',
      opacity: '1',
      padding: '4px 8px',
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: '700',
      fontFamily: 'Neue Haas Grotesk Text Std',
      backgroundColor: '#000000',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      whiteSpace: 'normal',
      maxWidth: '236px',
      boxSizing: 'border-box'
    };

    Object.assign(tooltip.style, styles);
  }
}
